import { Coverage } from 'src/app/core/types/equipment';
import { MarkupType } from 'src/app/shared/services/quote.service';
import { SortAccessors, sortByAccessor } from 'src/app/shared/utils/sortable-table-data-source';
import { ClaimByLimit, CoverageListItem, CoverageListSelectedFilters, RowMarkupOverride } from './models/list-coverage-response.model';
import { DealerMarkUp, MonthsHoursPair, PlanOption, PlanOptionsFilterValue } from './models/plan-option.model';
import { MarkupTypeMultiTerm } from '../quote-details.types';

/**
 * Raw shape currently returned by `CoverageTermService.getCoverageOptions()`
 * (GET api/v1/espp/coverage_terms). It carries no pricing today — this adapter
 * is a placeholder so the rest of the feature (filter/sort/markup) can be built
 * and tested now. Replace the body of this function once the backend for
 * WPAD-6518 defines the priced multi-option response shape; nothing else in
 * this feature should need to change.
 */
export interface RawCoverageTermOption {
    [key: string]: string | number | null | undefined;
}

export function mapCoverageTermsToPlanOptions(raw: RawCoverageTermOption[]): PlanOption[] {
    return raw
        .filter(option => !option.key)
        .map(option => {
            const coverage = String(option.coverage ?? '');
            return {
                coverage,
                displayLabel: coverage,
                coverageType: deriveCoverageType(coverage),
                months: Number(option.term ?? 0),
                hours: Number(option.hours ?? 0),
                serviceFee: Number(option.service_fee ?? 0),
                dealerTotal: Number(option.dealer_total ?? 0),
                dealerMarkUp: 0,
                customerTotal: Number(option.dealer_total ?? 0),
                ttm: null,
                hasPricing: false,
            };
        });
}

/**
 * Adapts the quote's actual EW coverages (already-priced, already on the quote)
 * into the PlanOption shape, so the table can show what the quote currently has
 * instead of the unpriced term/hours combinations from coverage_terms.
 */
export function mapEwCoveragesToPlanOptions(coverages: Coverage[]): PlanOption[] {
    return coverages.map(coverage => {
        const hours = coverage.hours === 'Unlimited' ? UNLIMITED_HOURS_THRESHOLD : Number(coverage.hours ?? 0);
        const dealerTotal = Number(coverage.premium ?? coverage.retail_cost ?? 0);
        const customerTotal = Number(coverage.customer_cost ?? dealerTotal);
        return {
            coverage: coverage.coverage,
            displayLabel: coverage.coverage,
            coverageType: deriveCoverageType(coverage.coverage),
            months: Number(coverage.term ?? 0),
            hours,
            serviceFee: 0,
            dealerTotal,
            dealerMarkUp: customerTotal - dealerTotal,
            customerTotal,
            ttm: null,
            hasPricing: true,
        };
    });
}

/**
 * Merges the full set of term/hours combinations from coverage_terms with the
 * quote's actual priced EW coverages: a term row that matches an already-priced
 * coverage (same coverage/months/hours) picks up its real pricing, everything
 * else stays unpriced (`hasPricing: false`) until the backend for WPAD-6518
 * returns pricing for every combination. Priced coverages with no matching term
 * row (shouldn't normally happen) are appended so a currently-applied coverage
 * is never dropped from the table.
 */
export function mergeOptionsWithPricing(termOptions: PlanOption[], pricedOptions: PlanOption[]): PlanOption[] {
    const key = (option: Pick<PlanOption, 'coverage' | 'months' | 'hours'>) => `${option.coverage}|${option.months}|${option.hours}`;
    const pricedByKey = new Map(pricedOptions.map(option => [key(option), option]));
    const matchedKeys = new Set<string>();

    const merged = termOptions.map(termOption => {
        const priced = pricedByKey.get(key(termOption));
        if (!priced) {
            return { ...termOption, hasPricing: false };
        }
        matchedKeys.add(key(termOption));
        return {
            ...termOption,
            serviceFee: priced.serviceFee,
            dealerTotal: priced.dealerTotal,
            dealerMarkUp: priced.dealerMarkUp,
            customerTotal: priced.customerTotal,
            hasPricing: true,
        };
    });

    const unmatchedPriced = pricedOptions.filter(option => !matchedKeys.has(key(option)));
    return [...merged, ...unmatchedPriced];
}

/** Whether TTM / TTM-MFR are part of the filter the current list-coverages rows were fetched with. */
export interface IncludedTtm {
    ttm: boolean;
    ttmMfr: boolean;
}

export const NO_TTM_INCLUDED: IncludedTtm = { ttm: false, ttmMfr: false };

/** TTM-MFR can only be included on top of TTM. */
export function toIncludedTtm(ttm: { enabled?: boolean | null; during_mfr_warranty?: boolean | null } | null | undefined): IncludedTtm {
    const ttmIncluded = Boolean(ttm?.enabled);
    return { ttm: ttmIncluded, ttmMfr: ttmIncluded && Boolean(ttm?.during_mfr_warranty) };
}

/**
 * Adapts `GET api/v2/quotes/{quote_id}/list-coverages`'s `data` (WPAD-6368) —
 * already priced per coverage/term/hours combination — directly into PlanOption
 * rows. Unlike mergeOptionsWithPricing's placeholder pipeline, every row here is
 * priced, so there's nothing to merge.
 */
export function mapListCoverageToPlanOptions(items: CoverageListItem[], included: IncludedTtm = NO_TTM_INCLUDED): PlanOption[] {
    return items.map(item => {
        // The backend sends ttm_price/ttm_mfr_price on every row, so they only count once TTM/TTM-MFR
        // is actually included in the applied filter.
        const dealerTotal = Number(item.premium ?? 0)
            + (included.ttm ? Number(item.ttm_price ?? 0) : 0)
            + (included.ttmMfr ? Number(item.ttm_mfr_price ?? 0) : 0);
        const customerTotal = Number(item.customer_cost ?? dealerTotal);
        return {
            coverage: item.coverage,
            displayLabel: item.display_label ?? item.coverage,
            coverageType: deriveCoverageType(item.coverage),
            months: Number(item.term ?? 0),
            hours: Number(item.hours ?? 0),
            serviceFee: Number(item.service_fee ?? 0),
            dealerTotal,
            dealerMarkUp: customerTotal - dealerTotal,
            customerTotal,
            ttm: null,
            hasPricing: true,
            rowKey: item.row_key,
        };
    });
}

/**
 * Raw TTM claim-limit shape returned by the equipment rate table
 * (`Rate.ttm` in `src/app/core/types/equipment.ts`) via `TtmLimitService`
 * (`GET api/v1/espp/ttm`) — not yet wired into this feature. This adapter
 * isolates that raw field-naming knowledge (`limit`/`total_limit`) in one
 * place so wiring in the live service later only means changing whatever
 * produces `RawTtmClaimLimitOption[]`; nothing else in this feature
 * (TtmClaimLimitFiltersComponent, its parents) needs to change.
 */
export interface RawTtmClaimLimitOption {
    cost: number;
    coverage: string;
    limit: number;
    total_limit: number;
}

export function deriveAvailableClaimLimits(raw: RawTtmClaimLimitOption[]): { singleClaimLimits: number[]; totalClaimLimits: number[] } {
    return {
        singleClaimLimits: [...new Set(raw.map(option => option.limit))],
        totalClaimLimits: [...new Set(raw.map(option => option.total_limit))],
    };
}

export function deriveCoverageType(coverage: string): string {
    return coverage?.split('&')?.[0]?.trim() ?? '';
}

export function deriveCoverageTypes(options: PlanOption[]): string[] {
    return [...new Set(options.map(option => option.coverageType))];
}

/**
 * Any sentinel a backend uses to mean "no limit" (9999, 999999, etc.) is
 * treated as unlimited via a threshold rather than an exact match, so a new
 * sentinel value doesn't require a code change here.
 */
export const UNLIMITED_HOURS_THRESHOLD = 9999;

export function isUnlimitedHours(hours: number): boolean {
    return hours >= UNLIMITED_HOURS_THRESHOLD;
}

/**
 * Badge styling is assigned from a fixed-size palette by the order in which
 * distinct coverage types appear, never from the coverage name itself — a new
 * coverage type added by the backend just takes the next palette slot instead
 * of needing a code change. Beyond COVERAGE_BADGE_PALETTE_SIZE distinct types
 * the index wraps around and two coverage types will share a look; this is an
 * intentional, documented fallback (see plan-options.util.spec.ts), not a bug.
 */
export const COVERAGE_BADGE_PALETTE_SIZE = 10;

export function buildCoverageBadgeVariantMap(options: PlanOption[]): Record<string, number> {
    const distinctTypes = deriveCoverageTypes(options);
    return distinctTypes.reduce((map, coverageType, index) => {
        map[coverageType] = index % COVERAGE_BADGE_PALETTE_SIZE;
        return map;
    }, {} as Record<string, number>);
}

export function deriveMonthsHoursPairs(options: PlanOption[]): MonthsHoursPair[] {
    const seen = new Set<string>();
    const pairs: MonthsHoursPair[] = [];
    options.forEach(option => {
        const key = `${option.months}/${option.hours}`;
        if (!seen.has(key)) {
            seen.add(key);
            pairs.push({ months: option.months, hours: option.hours });
        }
    });
    return pairs;
}

export type SortDirection = 'asc' | 'desc' | '';
export type PlanOptionColumnId =
    'select' | 'coverage' | 'months' | 'hours' | 'serviceFee' | 'dealerTotal' | 'dealerMarkUp' | 'customerTotal';
export type PlanOptionSortAccessor = (option: PlanOption) => string | number;

export interface PlanOptionColumnConfig {
    id: PlanOptionColumnId;
    /** Absent means the column isn't sortable. */
    sortAccessor?: PlanOptionSortAccessor;
}

/**
 * Single source of truth for both which columns render and how each one
 * sorts, so the two can't drift apart the way a separate displayedColumns
 * array and sort-accessor map could.
 */
export const DEFAULT_PLAN_OPTION_COLUMNS: PlanOptionColumnConfig[] = [
    { id: 'select' },
    { id: 'coverage', sortAccessor: option => option.displayLabel || option.coverage },
    { id: 'months', sortAccessor: option => option.months },
    { id: 'hours', sortAccessor: option => option.hours },
    { id: 'serviceFee' },
    { id: 'dealerTotal', sortAccessor: option => option.dealerTotal },
    { id: 'dealerMarkUp' },
    { id: 'customerTotal' },
];

export function toSortAccessors(columns: PlanOptionColumnConfig[]): SortAccessors<PlanOption> {
    const accessors: SortAccessors<PlanOption> = {};
    columns.forEach(column => {
        if (column.sortAccessor) accessors[column.id] = column.sortAccessor;
    });
    return accessors;
}

export function sortPlanOptions(
    options: PlanOption[],
    active: string | null | undefined,
    direction: SortDirection,
    columns: PlanOptionColumnConfig[] = DEFAULT_PLAN_OPTION_COLUMNS
): PlanOption[] {
    return sortByAccessor(options, active, direction, toSortAccessors(columns));
}

/**
 * Percent -> round(dealerTotal * (1 + amount/100)); Flat -> dealerTotal + amount.
 * No markup in effect (null, or amount 0 — the table's default before the user
 * touches the markup form) returns `currentCustomerTotal` as-is rather than
 * collapsing to dealerTotal: list-coverage rows (WPAD-6368) arrive already priced
 * with dealerTotal != customerTotal (the backend's own markup baked in), and that
 * must survive until the user explicitly dials in an override. Mirrors the
 * existing calc in QuoteDetailsComponent's markupSubject pipe so both the live
 * client-side table recompute and the eventual server sync agree.
 */
export function calculateCustomerTotal(dealerTotal: number, markup: DealerMarkUp | null, currentCustomerTotal: number = dealerTotal): number {
    if (!markup || !markup.type || !markup.amount) {
        return currentCustomerTotal;
    }
    if (markup.type === MarkupTypeMultiTerm.percentage) {
        return Math.round(dealerTotal * (1 + markup.amount / 100));
    }
    return dealerTotal + markup.amount;
}

export function applyDealerMarkUp(options: PlanOption[], markup: DealerMarkUp | null): PlanOption[] {
    return options.map(option => {
        if (option.hasPricing === false) {
            return option;
        }
        const customerTotal = calculateCustomerTotal(option.dealerTotal, markup, option.customerTotal);
        return {
            ...option,
            dealerMarkUp: customerTotal - option.dealerTotal,
            customerTotal,
        };
    });
}

/**
 * Body for `POST api/v2/quotes/{quote_id}/list-coverages` (WPAD-6368). Filtering now happens
 * server-side, so the live PlanOptionsFilterValue is translated into the same snake_case shape
 * the response echoes back as `selected_filters`.
 */
export interface ListCoverageRequest {
    coverage_types: string[];
    term_hours_pairs: { term: number; hours: number }[];
    ttm: {
        enabled: boolean;
        during_mfr_warranty: boolean;
        claim_limit: number | null;
        total_limit: number | null;
    };
    /** Dealer mark-up, applied server-side to customer_cost; both null when none is set. */
    markup: { type: MarkupTypeMultiTerm | null; amount: number | null; overrides: RowMarkupOverride[] };
}

/** Flat-fee override for one row: its edited customer total minus its dealer total, rounded to cents. */
export function toRowMarkupOverride(rowKey: string, dealerTotal: number, customerTotal: number): RowMarkupOverride {
    return { row_key: rowKey, type: 'flat_fee', amount: Math.round((customerTotal - dealerTotal) * 100) / 100 };
}

/** Adds/replaces `override` (matched by row_key) without mutating `overrides`; earlier overrides are kept. */
export function upsertRowOverride(overrides: RowMarkupOverride[], override: RowMarkupOverride): RowMarkupOverride[] {
    return [...overrides.filter(existing => existing.row_key !== override.row_key), override];
}

export function toListCoverageRequest(filter: PlanOptionsFilterValue, markup: DealerMarkUp | null = null, overrides: RowMarkupOverride[] = []): ListCoverageRequest {
    // TTM (and its during-MFR flag) only narrows the results once both claim limits are chosen.
    const ttmApplied = filter.includeTtm && filter.singleClaimLimit != null && filter.totalClaimLimit != null;
    return {
        coverage_types: filter.coverageTypes,
        term_hours_pairs: filter.monthsHoursPairs.map(pair => ({ term: pair.months, hours: pair.hours })),
        ttm: {
            enabled: ttmApplied,
            during_mfr_warranty: ttmApplied && filter.duringMfrWarranty,
            claim_limit: ttmApplied ? filter.singleClaimLimit : null,
            total_limit: ttmApplied ? filter.totalClaimLimit : null,
        },
        markup: {
            type: markup?.type && markup.amount ? markup.type : null,
            amount: markup?.type && markup.amount ? markup.amount : null,
            overrides,
        },
    };
}

/** `selected_filters` row overrides (under `markup.overrides`, or the sibling `row_overrides`) back into request form. */
export function fromSelectedOverrides(selected: Partial<CoverageListSelectedFilters> | null | undefined): RowMarkupOverride[] {
    const raw = selected?.markup?.overrides ?? selected?.row_overrides;
    return Array.isArray(raw) ? raw.filter(item => item?.row_key != null && item.amount != null).map(item => ({ row_key: item.row_key, type: 'flat_fee' as const, amount: Number(item.amount) })) : [];
}

/** Inverse of the request's `markup`: `selected_filters.markup` back into the markup form's value (null = none). */
export function fromSelectedMarkup(selected: Partial<CoverageListSelectedFilters> | null | undefined): DealerMarkUp | null {
    const amount = Number(selected?.markup?.amount);
    return selected?.markup?.type && amount ? { type: selected.markup.type, amount } : null;
}

/**
 * Inverse of toListCoverageRequest: turns the response's `selected_filters` into the live filter,
 * used to pre-populate the filter UI on first load. `selected` is a system-boundary value, so every
 * field is read defensively.
 */
export function fromSelectedFilters(selected: Partial<CoverageListSelectedFilters> | null | undefined): PlanOptionsFilterValue {
    const pairs = Array.isArray(selected?.term_hours_pairs) ? selected!.term_hours_pairs! : [];
    const toNumberOrNull = (value: unknown): number | null => value != null && value !== '' && !isNaN(Number(value)) ? Number(value) : null;
    return {
        coverageTypes: Array.isArray(selected?.coverage_types) ? selected!.coverage_types! : [],
        monthsHoursPairs: pairs.map(pair => ({ months: Number(pair.term), hours: Number(pair.hours) })),
        includeTtm: Boolean(selected?.ttm?.enabled),
        duringMfrWarranty: Boolean(selected?.ttm?.during_mfr_warranty),
        singleClaimLimit: toNumberOrNull(selected?.ttm?.claim_limit),
        totalClaimLimit: toNumberOrNull(selected?.ttm?.total_limit),
    };
}

export interface ClaimLimitChoices {
    singleClaimLimits: number[];
    totalClaimLimits: number[];
}

/**
 * Single/total claim limit dropdown options from list-coverages' `filters.ttm.claim_by_limit`
 * (`{ [months]: { [singleLimit]: totalLimits[] } }`). Only the selected pairs' months contribute —
 * with several pairs the options are merged (union), and with none every month contributes. A chosen
 * single limit narrows the total limits to the ones offered under it.
 */
export function deriveClaimLimitChoices(
    claimByLimit: ClaimByLimit | null | undefined,
    pairs: MonthsHoursPair[],
    singleClaimLimit: number | null = null
): ClaimLimitChoices {
    if (!claimByLimit) return { singleClaimLimits: [], totalClaimLimits: [] };
    const selectedMonths = new Set(pairs.map(pair => String(pair.months)));
    const months = Object.keys(claimByLimit).filter(month => selectedMonths.size === 0 || selectedMonths.has(month));
    const singles = new Set<number>();
    const totals = new Set<number>();
    months.forEach(month => {
        Object.entries(claimByLimit[month] ?? {}).forEach(([single, totalLimits]) => {
            singles.add(Number(single));
            if (singleClaimLimit == null || Number(single) === singleClaimLimit) {
                (totalLimits ?? []).forEach(total => totals.add(Number(total)));
            }
        });
    });
    const ascending = (a: number, b: number) => a - b;
    return { singleClaimLimits: [...singles].sort(ascending), totalClaimLimits: [...totals].sort(ascending) };
}

/**
 * Drops TTM selections that stopped being valid after another filter changed: with TTM off the
 * during-MFR flag and both limits are cleared, and a limit missing from the current choices is
 * replaced by the first available choice (with TTM on, both limits are always preselected). Returns the same object when nothing changed.
 */
export function sanitizeTtmFilter(filter: PlanOptionsFilterValue, claimByLimit: ClaimByLimit | null | undefined): PlanOptionsFilterValue {
    if (!filter.includeTtm) {
        return filter.duringMfrWarranty || filter.singleClaimLimit != null || filter.totalClaimLimit != null
            ? { ...filter, duringMfrWarranty: false, singleClaimLimit: null, totalClaimLimit: null }
            : filter;
    }
    if (!claimByLimit) return filter;
    const { singleClaimLimits } = deriveClaimLimitChoices(claimByLimit, filter.monthsHoursPairs);
    const singleClaimLimit = filter.singleClaimLimit != null && singleClaimLimits.includes(filter.singleClaimLimit) ? filter.singleClaimLimit : singleClaimLimits[0] ?? null;
    const { totalClaimLimits } = deriveClaimLimitChoices(claimByLimit, filter.monthsHoursPairs, singleClaimLimit);
    const totalClaimLimit = filter.totalClaimLimit != null && totalClaimLimits.includes(filter.totalClaimLimit) ? filter.totalClaimLimit : totalClaimLimits[0] ?? null;
    return singleClaimLimit === filter.singleClaimLimit && totalClaimLimit === filter.totalClaimLimit
        ? filter
        : { ...filter, singleClaimLimit, totalClaimLimit };
}
